import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';

import type { PopupData, Service } from 'webapp-types';
import type { BillingFeature } from 'webapp/enums/enums';
import { PlanType, PromotionIdentifier } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { UserSettingsKey } from 'webapp/enums/user-settings';
import { getFormattedPrice } from 'webapp/utils/number';

import BarLoaderComponent from 'webapp/components/bar-loader';
import BillingPlanTopFeaturesComponent from 'webapp/components/billing/plan-top-features';
import BillingPopupPlanUpgradeBaseComponent from 'webapp/components/billing/popup-plan-upgrade-base';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type BillingPlan from 'webapp/models-custom/billing-plan';

import type { TrackingOriginTypeForTrialPopupOpened } from 'webapp/services/billing';

export default class BillingPopupPlanUpgradeOfferComponent<
	TData extends object = PopupData.Any,
> extends BillingPopupPlanUpgradeBaseComponent<TData> {
	@service('billing') declare billingService: Service.Billing;
	@service('flow/promotions') declare flowPromotionsService: Service.Flow.Promotions;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('request') declare requestService: Service.Request;
	@service('user-settings') declare userSettingsService: Service.UserSettings;

	promotionId = PromotionIdentifier.TRIAL_HIGHER_PLAN_OFFER;
	origin: TrackingOriginTypeForTrialPopupOpened = 'offer';

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const trackingData: {
			current_plan_id: string;
			origin: string;
			feature?: BillingFeature | TrackingOriginTypeForTrialPopupOpened;
		} = {
			current_plan_id: this.billingService.activePlan!.id,
			origin: this.origin,
		};

		const feature = this.feature;
		if (feature) {
			const trackingOriginForFeature = this.trackingOriginForFeature;
			if (trackingOriginForFeature) {
				trackingData.feature = trackingOriginForFeature;
			} else {
				trackingData.feature = feature;
			}
		}

		this.analyticsService.event(TrackingEvent.PLAN_TRIAL_POPUP_OPENED, trackingData);

		next(this, () => {
			void this.userSettingsService.save(UserSettingsKey.GROWTH_TRIAL_OFFER_POPUP_SEEN, true);
		});
	}

	get targetPlan(): BillingPlan | null {
		// current only for Growth
		return this.flowPromotionsService.getPlanByTypeForCurrentCycle(PlanType.GROWTH) || null;
	}

	get feature(): BillingFeature | undefined {
		return undefined;
	}

	get trackingOriginForFeature(): TrackingOriginTypeForTrialPopupOpened | null {
		return null;
	}

	get activePlan(): BillingPlan | null {
		return this.billingService.activePlan;
	}

	get onPlanUpgradeFn() {
		return undefined;
	}

	get upgradeText() {
		return htmlSafe('You are getting a free upgrade!');
	}

	get noticeText(): string | null {
		const activePlanName = this.activePlan?.name;
		return `Start your first month on ${this.planName}${activePlanName ? ` for a price of ${activePlanName}` : ''}`;
	}

	get buttonText() {
		return 'Try Growth';
	}

	get planPrice() {
		const activePlan = this.activePlan;

		if (!activePlan) {
			return null;
		}

		return getFormattedPrice(this.getMonthlyPrice(activePlan), 0, true);
	}

	get previousPlanPrice() {
		const availablePlans = this.billingService.availablePlans,
			growthMonthlyPlan = availablePlans.find((plan) => {
				return plan.isGrowth && plan.isMonthly;
			});

		if (!growthMonthlyPlan) {
			return;
		}

		return getFormattedPrice(growthMonthlyPlan?.price, 0, true);
	}

	get isPreviousPlanPriceShown() {
		return true;
	}

	get pricingTitle(): string | null {
		return null;
	}

	get pricingAdditionalText(): string {
		return `Then auto-renews for ${this.previousPlanPrice}/month.`;
	}

	getMonthlyPrice(plan: BillingPlan) {
		return plan.price / plan.monthsInCycle;
	}

	@action
	requestTrial(): void {
		this.submitInProgress = true;

		const planForTrial = this.targetPlan;
		if (planForTrial) {
			this.billingService
				.requestAutomatedPayingTrial(planForTrial.id, this.promotionId)
				.then(() => {
					this.close();
				})
				.finally(() => {
					this.submitInProgress = false;
				})
				.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
		}
	}
	<template>
		<div class="content content--split content--{{this.planType}}">
			<div class="content__left {{unless this.planHightlightText 'content__left--no-text'}}">
				<div class="content__image-container">
					<img src={{this.imageSrc}} alt="Features" />
				</div>
				<div class="content__text-container">
					<div class="content__text content__text--highlight">
						{{{this.planHightlightText}}}
					</div>
				</div>

			</div>
			<div class="content__right content__right--{{this.targetPlan.planType}}">
				<div class="content__text content__text--upgrade">{{this.upgradeText}}</div>
				{{#if this.noticeText}}
					<div class="content__text content__text--notice">{{this.noticeText}}</div>
				{{/if}}
				{{#if this.targetPlan}}
					<BillingPlanTopFeaturesComponent @model={{this.targetPlan}} />
				{{/if}}
				<div class="content__features">
					{{#each this.planFeatures as |feature|}}
						<div class="content__feature-item">
							<CommonUiIconComponent @name="Check" @size="md" />
							{{feature}}
						</div>
					{{/each}}
				</div>
				{{#if this.submitInProgress}}
					<div class="loader__wrapper">
						<BarLoaderComponent />
					</div>
				{{else}}
					{{#if this.planPrice}}
						{{#unless this.isPreviousPlanPriceShown}}
							<div class="content__pricing-title">{{this.pricingTitle}}</div>
						{{/unless}}
						<div class="content__pricing">
							{{#if this.isPreviousPlanPriceShown}}
								<span class="content__pricing-symbol">$</span>
								<span
									class="content__pricing-amount content__pricing-amount--previous"
								>
									{{this.previousPlanPrice}}
								</span>
							{{/if}}
							<span class="content__pricing-symbol">$</span>
							<span class="content__pricing-amount">{{this.planPrice}}</span>
							<span class="content__pricing-period">/month</span>
							<div class="content__pricing-additional">
								{{this.pricingAdditionalText}}<br />
								<CommonUiIconComponent @name="Mail" @size="sm" />We'll remind you 3
								days before your offer ends.
							</div>
						</div>
					{{/if}}
				{{/if}}
				<div class="content__terms">
					By accepting the offer, you agree to the
					<a href="https://databox.com/terms-of-service" target="_blank">Terms of Service</a>
					and authorize Databox to upgrade your plan to
					{{this.planName}}
					after your free trial ends. You can cancel anytime.
				</div>
				<div class="content__button-container">
					<SubmitButtonComponent
						@text={{this.buttonText}}
						@noDelay={{true}}
						@onTrigger={{this.requestTrial}}
					/>
					<SubmitButtonComponent
						@text="No thanks"
						@theme="blue-outlined"
						@onTrigger={{this.close}}
					/>
				</div>
			</div>
		</div>
	</template>
}
